/**
 * SWR Cache Key 生成ユーティリティ (Contest API 用)
 * 全ての contest 関連の SWR key をここで一元管理
 */

import { ContestSearchRequest } from "@/restapi/contest";

/**
 * コンテスト検索リスト用の cache key を生成
 * params の順序を固定化してキーの一貫性を保証する
 * 形式: contest-list?{normalized-params}&page={page}
 */
export function buildContestListKey(
  params: ContestSearchRequest,
  page: number = 1
): string {
  const sp = new URLSearchParams();

  // 重要: 順序を固定化（アルファベット順）
  sp.set("screen", params.screen);
  sp.set("per_page", String(params.perPage ?? 10));
  
  if (params.viewer) sp.set("viewer", params.viewer);
  if (params.isPublished !== undefined) sp.set("is_published", String(params.isPublished));
  if (params.isLocked !== undefined) sp.set("is_locked", String(params.isLocked));
  if (params.q) sp.set("q", params.q);
  if (params.groupId !== undefined) sp.set("group_id", String(params.groupId));
  if (params.startFrom) sp.set("start_from", params.startFrom);
  if (params.startTo) sp.set("start_to", params.startTo);
  if (params.sortBy) sp.set("sort_by", params.sortBy);
  if (params.sortOrder) sp.set("sort_order", params.sortOrder);
  if (params.actorUuid) sp.set("actor_uuid", params.actorUuid);

  // Array パラメータはソートして追加
  (params.statuses ?? [])
    .slice()
    .sort()
    .forEach((s) => sp.append("status[]", s));

  (params.categoryIds ?? [])
    .slice()
    .sort((a, b) => a - b)
    .forEach((id) => sp.append("category_ids[]", String(id)));

  (params.userIds ?? [])
    .slice()
    .sort()
    .forEach((id) => sp.append("user_ids[]", id));

  const paramsStr = sp.toString();
  return `contest-list?${paramsStr}&page=${page}`;
}

/**
 * 検索リスト key の base を取得（page なし）
 * mutate で "この params の全ページ" を再検証する際に使う可能性あり
 */
export function buildContestListKeyPrefix(
  params: ContestSearchRequest
): string {
  const sp = new URLSearchParams();

  // buildContestListKey と同じ順序で
  sp.set("screen", params.screen);
  sp.set("per_page", String(params.perPage ?? 10));
  
  if (params.viewer) sp.set("viewer", params.viewer);
  if (params.isPublished !== undefined) sp.set("is_published", String(params.isPublished));
  if (params.isLocked !== undefined) sp.set("is_locked", String(params.isLocked));
  if (params.q) sp.set("q", params.q);
  if (params.groupId !== undefined) sp.set("group_id", String(params.groupId));
  if (params.startFrom) sp.set("start_from", params.startFrom);
  if (params.startTo) sp.set("start_to", params.startTo);
  if (params.sortBy) sp.set("sort_by", params.sortBy);
  if (params.sortOrder) sp.set("sort_order", params.sortOrder);
  if (params.actorUuid) sp.set("actor_uuid", params.actorUuid);

  (params.statuses ?? [])
    .slice()
    .sort()
    .forEach((s) => sp.append("status[]", s));

  (params.categoryIds ?? [])
    .slice()
    .sort((a, b) => a - b)
    .forEach((id) => sp.append("category_ids[]", String(id)));

  (params.userIds ?? [])
    .slice()
    .sort()
    .forEach((id) => sp.append("user_ids[]", id));

  const paramsStr = sp.toString();
  return `contest-list?${paramsStr}`;
}

/**
 * 検索リスト key の正規化版を使って mutate を実行
 * 関連する全ページを再検証する
 */
export function mutateContestListByParams(
  mutateFn: any,
  params: ContestSearchRequest
): void {
  const prefix = buildContestListKeyPrefix(params);
  mutateFn((key: string) => typeof key === "string" && key.startsWith(prefix + "&page="));
}

/**
 * 全 contest-list key を再検証（params の条件にかかわらず）
 * 通常は使わない（非効率）、万が一の安全弁として用意
 */
export function mutateAllContestLists(
  mutateFn: any
): void {
  mutateFn((key: string) => typeof key === "string" && key.startsWith("contest-list?"));
}
